import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { TEmbeddingExternalReference, TSourceDetail } from '@core/interfaces/TMessage';

import { Conversation } from '../../../infrastructure/models/Conversation';
import { TAdaptedMessageV2, TAdaptedReferenceV3, TReferenceToDisplay } from '../../../types/types';
import { ReferenceDisplayPreferencesService } from '@shared/services/reference-display-preferences.service';

import { ReferenceTransformer } from '../../../services/adapters/transformers/reference-transformer';
import { NewSourcePreviewButtonComponent } from '../source-preview/new-button/new-source-preview-button.component';

/** Rows for external mode: bibliography blocks vs grouped citation-only lines. */
export type RefDisplayRow =
  | { kind: 'external'; child: TAdaptedReferenceV3; lines: TEmbeddingExternalReference[] }
  | { kind: 'inlineGroup'; children: TAdaptedReferenceV3[]; sharedLabel: string };

@Component({
    selector: 'app-references',
    templateUrl: './references.component.html',
    styleUrls: ['./references.component.scss'],
    standalone: true,
    imports: [MatIcon, NewSourcePreviewButtonComponent],
})
export class ReferencesComponent implements OnInit {
  private readonly _prefs = inject(ReferenceDisplayPreferencesService);
  private readonly _cdr = inject(ChangeDetectorRef);

  @Input() references: TReferenceToDisplay[] = [];
  /** When set with message, external mode can show per-number [n] + lists. */
  @Input() conversation: Conversation | null | undefined;
  @Input() message: TAdaptedMessageV2 | undefined;
  @Output() sourceSelected = new EventEmitter<TAdaptedReferenceV3>();

  ngOnInit(): void {
    this._prefs.ensureLoaded().subscribe(() => this._cdr.markForCheck());
  }

  protected showSourceReference(ref: TReferenceToDisplay): boolean {
    return this._prefs.shouldShowSourceReference() || !this.hasResolvedReferenceInReferenceRows(ref);
  }

  protected showReferenceInReferenceRows(ref: TReferenceToDisplay): boolean {
    return (
      !!this.conversation &&
      !!this.message &&
      this._prefs.shouldShowReferenceInReferenceRows(this.hasResolvedReferenceInReferenceRows(ref))
    );
  }

  protected panelLabel(ref: TReferenceToDisplay): string {
    return this._prefs.referencePanelLabel(ref.originalSource);
  }

  protected detailForChild(ref: TReferenceToDisplay, child: TAdaptedReferenceV3): TSourceDetail | undefined {
    if (!this.message || !this.conversation) {
      return ref.originalSource;
    }
    const sourceId = ref.originalSource._id;
    const embeddingId = child.referenceDto?.embeddingId;
    return ReferenceTransformer.getSourceDetailForPanel(
      this.message,
      this.conversation,
      sourceId,
      embeddingId
    );
  }

  /** Non-empty list → show bullets; null/empty → fallback line. */
  protected externalLinesForChild(
    ref: TReferenceToDisplay,
    child: TAdaptedReferenceV3
  ): TEmbeddingExternalReference[] | null {
    const d = this.detailForChild(ref, child);
    const ext = this._prefs.dedupeExternalReferences(d?.embeddingExternalReferences);
    if (!ext?.length) {
      return null;
    }
    return ext;
  }

  protected fallbackLabelForChild(ref: TReferenceToDisplay, child: TAdaptedReferenceV3): string {
    const d = this.detailForChild(ref, child);
    return d?.citation || ref.originalSource.citation || ref.originalSource.name;
  }

  protected formatExtLine(ext: TEmbeddingExternalReference): string {
    return this._prefs.formatExternalReferenceLine(ext);
  }

  protected externalRowLabel(child: TAdaptedReferenceV3, extIndex: number): string | null {
    return child.uiDisplayLabels?.[extIndex] ?? child.uiDisplayLabel ?? null;
  }

  protected externalLineUrl(ext: TEmbeddingExternalReference): string | null {
    return this._prefs.externalReferenceUrl(ext);
  }

  /**
   * Merge consecutive citation-only children that share the same label (e.g. [14][15][16] → one title, many buttons).
   * Children with embedding external reference lists stay on their own rows.
   */
  protected rowsForRef(ref: TReferenceToDisplay): RefDisplayRow[] {
    const rows: RefDisplayRow[] = [];
    const children = ref.children;
    let i = 0;
    while (i < children.length) {
      const lines = this.externalLinesForChild(ref, children[i]);
      if (lines?.length) {
        rows.push({ kind: 'external', child: children[i], lines });
        i += 1;
        continue;
      }
      const label = this.fallbackLabelForChild(ref, children[i]);
      const group: TAdaptedReferenceV3[] = [children[i]];
      let j = i + 1;
      while (j < children.length) {
        const nextLines = this.externalLinesForChild(ref, children[j]);
        if (nextLines?.length) {
          break;
        }
        const nextLabel = this.fallbackLabelForChild(ref, children[j]);
        if (this.citationKey(nextLabel) !== this.citationKey(label)) {
          break;
        }
        group.push(children[j]);
        j += 1;
      }
      rows.push({ kind: 'inlineGroup', children: group, sharedLabel: label });
      i = j;
    }
    return rows.filter(row => row.kind === 'external' || this._prefs.shouldShowSourceReference());
  }

  protected trackRefRow(_index: number, row: RefDisplayRow): string {
    if (row.kind === 'external') {
      return `e-${row.child.listPositionIndex ?? _index}`;
    }
    return `g-${row.children.map(c => c.listPositionIndex ?? '?').join('-')}`;
  }

  private hasResolvedReferenceInReferenceRows(ref: TReferenceToDisplay): boolean {
    return ref.children.some(child => !!this.externalLinesForChild(ref, child)?.length);
  }

  private citationKey(s: string): string {
    return s.trim().replace(/\s+/g, ' ');
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackBySourceId(index: number, item: TAdaptedReferenceV3): string {
    return item.sourceId || index.toString();
  }
}
